const replaceParams = (template: string, params: Record<string, unknown> = {}) => {
  return Object.keys(params).reduce(
    (acc, key) => acc.replace(`:${key}`, String(params[key])),
    template.replace('*', ''),
  );
};
// Based on https://davidtimms.github.io/programming-languages/typescript/2020/11/20/exploring-template-literal-types-in-typescript-4.1.html
type PathParams<Path extends string> = Path extends `:${infer Param}/${infer Rest}`
  ? Param | PathParams<Rest>
  : Path extends `:${infer Param}`
    ? Param
    : Path extends `${infer _Prefix}:${infer Rest}`
      ? PathParams<`:${Rest}`>
      : never;

type PathArgs<Path extends string> = Record<PathParams<Path>, string>;

const defineRoute = <P extends string>(template: P, options: { exact: boolean } = { exact: true }) => {
  type Params = PathParams<P>;
  return {
    template: options.exact ? template : `${template}/*`,
    buildLink: (params: Params extends never ? void : PathArgs<P>) =>
      `/${replaceParams(template, params as PathArgs<P> | undefined)}`,
    buildLinkWithChildPath: (childPath: string, params: Params extends never ? void : PathArgs<P>) =>
      `/${replaceParams(template, params as PathArgs<P> | undefined)}/${childPath}`,
  } as const;
};

export const RouteConfig = {
  Login: defineRoute('login'),
  PasskeySetup: defineRoute('passkey-setup'),
  TermsAcceptance: defineRoute('terms-acceptance'),
  SignUp: defineRoute('signup'),
  SignUpEmailVerification: defineRoute('signup/email-verification'),
  SignUpUser: defineRoute('signup/user'),
  SignUpAddCompany: defineRoute('signup/add-company'),
  SignUpPlanSummary: defineRoute('signup/summary'),
  SignUpJoinSummary: defineRoute('signup/summary-join'),
  SignUpWelcome: defineRoute('signup/welcome'),
  CompanyRedirect: defineRoute('company-redirect/:id'),
  ChangePassword: defineRoute('change-password'),
  ResetPassword: defineRoute('reset-password'),
  AcceptInvite: defineRoute('accept-invite'),
  DownloadApp: defineRoute('app'),
  DownloadBookkeepingLedger: defineRoute('download-bookkeeping-ledger'),
  SharedBookkeepingLedger: defineRoute('share-bookkeeping-ledger/:token'),
  SharedInvoice: defineRoute('share-invoice/:token'),
  DownloadProvisionalBalance: defineRoute('download-provisional-balance'),
  SharedProvisionalBalance: defineRoute('share-provisional-balance/:token'),
  AppRoot: defineRoute('*'),
  Accounts: defineRoute('accounts', { exact: false }),
  CashbackInfo: defineRoute('accounts/cashback'),
  AccountsInfo: defineRoute('accounts/info'),
  AccountsCreditLines: defineRoute('accounts/credit-lines'),
  AccountsCards: defineRoute('accounts/cards'),
  AccountsNewCards: defineRoute('accounts/cards/new'),
  AccountsNewConsent: defineRoute('accounts/new'),
  AccountsCardDetails: defineRoute('accounts/cards/:cardId'),
  UserBankAccountsOnboarding: defineRoute('accounts/user-bank-accounts/onboarding'),
  Reconciliations: defineRoute('reconciliations'),
  Transactions: defineRoute('transactions', { exact: false }),
  Movements: defineRoute('transactions/movements'),
  TransactionRules: defineRoute('transactions/rules'),
  Payments: defineRoute('transactions/payments'),
  Cashflow: defineRoute('cashflow', { exact: false }),
  Outstanding: defineRoute('outstanding', { exact: false }),
  OutstandingFlows: defineRoute('outstanding/flows'),
  OutstandingFlowsInflow: defineRoute('outstanding/flows/inflow'),
  OutstandingFlowsOutflow: defineRoute('outstanding/flows/outflow'),
  OutstandingFlowsAll: defineRoute('outstanding/flows/all'),
  OutstandingSummary: defineRoute('outstanding/summary'),
  Recurrences: defineRoute('outstanding/recurrences'),
  OutstandingRules: defineRoute('outstanding/rules'),
  PayOutstandingFlows: defineRoute('outstanding/flows/pay'),
  DownloadXMLOutstandingFlows: defineRoute('outstanding/flows/download-xml'),
  RemittanceOutstandingFlows: defineRoute('outstanding/flows/remittance'),
  RemittanceOutstandingFlowsSuccess: defineRoute('outstanding/flows/remittance/success'),
  OutstandingAutomaticPaymentReminders: defineRoute('outstanding/automatic-payment-reminders'),
  Invoices: defineRoute('invoices', { exact: false }),
  InvoicesBills: defineRoute('invoices/bills'),
  InvoicesBuilder: defineRoute('invoices/builder'),
  InvoicesDashboard: defineRoute('invoices/dashboard'),
  InvoicesDdt: defineRoute('invoices/ddt'),
  InvoicesQuotes: defineRoute('invoices/quotes'),
  InvoicesImport: defineRoute('invoices/import'),
  InvoicesImportPDF: defineRoute('invoices/import-pdf'),
  InvoicesInfo: defineRoute('invoices/info'),
  InvoicesIssued: defineRoute('invoices/issued'),
  InvoicesProductCatalog: defineRoute('invoices/product-catalog'),
  InvoicesReceived: defineRoute('invoices/received'),
  InvoicesDocumentLineCostCenterAllocation: defineRoute('invoices/documents/:documentId/cost-center-allocations'),
  Counterparts: defineRoute('counterparts'),
  Settings: defineRoute('settings', { exact: false }),
  SettingsTeam: defineRoute('settings/team'),
  SettingsTeamNew: defineRoute('settings/team/user/new'),
  SettingsTeamEdit: defineRoute('settings/team/user/:companyUserId'),
  SettingsSecurity: defineRoute('settings/security'),
  SettingsProfile: defineRoute('settings/profile'),
  SettingsApiKey: defineRoute('settings/api-key'),
  SettingsFeqCredential: defineRoute('settings/feq-credential'),
  ConsentsRedirect: defineRoute('consent/callback'),
  Error: defineRoute('error/:error'),
  NotFound: defineRoute('404'),
  Logout: defineRoute('logout'),
  UnauthorisedCompany: defineRoute('unauthorised-company'),
  ExpiredSubscription: defineRoute('expired-subscription'),
  PaymentsRedirect: defineRoute('payment/callback'),
  PaymentsPdf: defineRoute('payment/pdf'),
  PaymentsBulk: defineRoute('payment/bulk'),
  PaymentsBulkDraft: defineRoute('payment/bulk/:draftId'),
  PaymentsBulkPdf: defineRoute('payment/bulk/pdf'),
  Referral: defineRoute('referral', { exact: false }),
  Bookkeeping: defineRoute('bookkeeping', { exact: false }),
  BookkeepingBooks: defineRoute('bookkeeping/books'),
  BookkeepingDocuments: defineRoute('bookkeeping/documents'),
  BookkeepingVATSettlement: defineRoute('bookkeeping/vat-settlement'),
  BookkeepingLIPE: defineRoute('bookkeeping/lipe'),
  BookkeepingWithholdingTax: defineRoute('bookkeeping/withholding-tax'),
  BookkeepingIntrastat: defineRoute('bookkeeping/intrastat'),
  BookkeepingEditIntrastatDeclaration: defineRoute('bookkeeping/intrastat/:declarationId/edit'),
  BookkeepingFinancialStatement: defineRoute('bookkeeping/financial-statement'),
  BookkeepingFinancialStatementDetail: defineRoute('bookkeeping/financial-statement/:financialStatementId'),
  BookkeepingFinancialStatementAccountMapping: defineRoute('bookkeeping/financial-statement/account-mapping'),
  BookkeepingVATDeclaration: defineRoute('bookkeeping/vat-declaration'),
  BookkeepingVATDeclarationDetail: defineRoute('bookkeeping/vat-declaration/:declarationId'),
  BookkeepingVATDeclarationSection: defineRoute('bookkeeping/vat-declaration/:declarationId/:section'),
  BookkeepingDeclaration770: defineRoute('bookkeeping/770'),
  BookkeepingDeclaration770Detail: defineRoute('bookkeeping/770/:declarationId'),
  BookkeepingDeclaration770Section: defineRoute('bookkeeping/770/:declarationId/:section'),
  BookkeepingCUDeclaration: defineRoute('bookkeeping/cu-declarations'),
  BookkeepingCUDeclarationDetail: defineRoute('bookkeeping/cu-declarations/:cuDeclarationId'),
  BookkeepingCUDeclarationFrontespizio: defineRoute('bookkeeping/cu-declarations/:cuDeclarationId/frontespizio'),
  BookkeepingIncomeTaxDeclaration: defineRoute('bookkeeping/income-tax-declaration'),
  BookkeepingIncomeTaxDeclarationDetail: defineRoute('bookkeeping/income-tax-declaration/:declarationId'),
  BookkeepingIncomeTaxDeclarationSection: defineRoute(
    'bookkeeping/income-tax-declaration/:declarationId/:model/:section',
  ),
  BookkeepingCUDeclarationRecipient: defineRoute(
    'bookkeeping/cu-declarations/:cuDeclarationId/recipients/:recipientId',
  ),
  BookkeepingIntrastatPreview: defineRoute('intrastat-preview/:declarationId'),
  BookkeepingNewBillsVATRegistrations: defineRoute('bookkeeping/new-bills-vat-registrations/:year/:month/:source'),
  BookkeepingEditBillVATRegistration: defineRoute('bookkeeping/edit-bill-vat-registration/:documentId'),
  BookkeepingNewVATRegistration: defineRoute('bookkeeping/new-vat-registration/:documentId'),
  BookkeepingNewManualRegistration: defineRoute('bookkeeping/new-registration'),
  BookkeepingEditVATRegistration: defineRoute('bookkeeping/edit-vat-registration/:documentId'),
  BookkeepingEditManualVATRegistration: defineRoute('bookkeeping/edit-manual-vat-registration/:registrationId'),
  GeneralBookkeepingTransactions: defineRoute('bookkeeping/general/transactions'),
  GeneralBookkeepingNonElectronicDocuments: defineRoute('bookkeeping/general/non-electronic-documents'),
  BookkeepingPayrollRegistration: defineRoute('bookkeeping/new-payroll-registration/:documentId'),
  BookkeepingReceiptRegistration: defineRoute('bookkeeping/new-receipt-registration/:documentId'),
  GeneralBookkeepingLedgerCards: defineRoute('bookkeeping/general/ledger-cards'),
  GeneralBookkeepingCashBook: defineRoute('bookkeeping/general/cash-book'),
  GeneralBookkeepingProvisionalBalance: defineRoute('bookkeeping/general/provisional-balance'),
  GeneralBookkeepingFixedAssets: defineRoute('bookkeeping/general/fixed-assets'),
  GeneralBookkeepingFixedAssetsRegister: defineRoute('bookkeeping/general/fixed-assets/register'),
  BookkeepingTaxDebitCredits: defineRoute('bookkeeping/debits-credits'),
  BookkeepingF24: defineRoute('bookkeeping/f24'),
  BookkeepingF24Preview: defineRoute('f24-preview/:id'),
  BookkeepingSettings: defineRoute('bookkeeping/settings'),
  Analytics: defineRoute('analytics', { exact: false }),
  AnalyticsPurchases: defineRoute('analytics/purchases', { exact: false }),
  AnalyticsPurchasesProductsPriceChanges: defineRoute('analytics/purchases/products-price-changes'),
  AnalyticsItemVolumes: defineRoute('analytics/purchases/item-volumes'),
  AnalyticsAveragePrices: defineRoute('analytics/purchases/average-prices'),
  AnalyticsItemQuantities: defineRoute('analytics/purchases/item-quantities'),
  AnalyticsSales: defineRoute('analytics/sales', { exact: false }),
  AnalyticsSalesProductsPriceChanges: defineRoute('analytics/sales/products-price-changes'),
  AnalyticsSalesItemVolumes: defineRoute('analytics/sales/item-volumes'),
  AnalyticsSalesItemQuantities: defineRoute('analytics/sales/item-quantities'),
  AnalyticsSalesAveragePrices: defineRoute('analytics/sales/average-prices'),
  AnalyticsBills: defineRoute('analytics/bills', { exact: false }),
  AnalyticsBillsTaxableByDevice: defineRoute('analytics/bills/indicators/taxable-by-device'),
  AnalyticsBillsNonTaxableSales: defineRoute('analytics/bills/indicators/non-taxable-sales'),
  AnalyticsBillsPaymentMethods: defineRoute('analytics/bills/indicators/payment-methods'),
  AnalyticsBillsAverageTicket: defineRoute('analytics/bills/indicators/average-ticket'),
  AnalyticsBillsClosingDetails: defineRoute('analytics/bills/closing-details'),
  AnalyticsBillsMonthlyDetails: defineRoute('analytics/bills/monthly-details'),
  Categories: defineRoute('cashflow/categories'),
  ActionConsentCallback: defineRoute('action-consent/callback'),
  F24: defineRoute('f24'),
  F24Pay: defineRoute('f24/pay'),
  F24Upload: defineRoute('f24/upload'),
  Chat: defineRoute('chat', { exact: false }),
  ChatNew: defineRoute('chat/new'),
  ChatDetail: defineRoute('chat/:chatId'),
  ChatTerms: defineRoute('chat/terms'),
};
